import React from "react";
import { Card } from "@mui/material";
import { styled } from "@mui/material/styles";
import { LocationStatus, LOCATION_STATUS_STYLES } from "../../../types/LocationStatus";
import { LocationCardContext } from "./LocationCardContext";
import LocationCardImage from "./LocationCardImage";
import LocationCardContent from "./LocationCardContent";
import LocationCardHeader from "./LocationCardHeader";
import LocationCardActions from "./LocationCardActions";
import LocationCardFields from "./LocationCardFields";
import LocationCardField from "./LocationCardField";
import LocationCardFieldGroup from "./LocationCardFieldGroup";
import LocationCardSearchChip from "./LocationCardSearchChip";
import LocationCardPendingNotice from "./LocationCardPendingNotice";
import LocationCardDraftProgress from "./LocationCardDraftProgress";
import LocationCardCapacityAlertBadge from "./LocationCardCapacityAlertBadge";

interface LocationCardProps {
  derivedStatus: LocationStatus;
  children: React.ReactNode;
}

const CardRoot = styled(Card, {
  shouldForwardProp: (prop) => prop !== "ownerStatus",
})<{ ownerStatus: LocationStatus }>(({ ownerStatus }) => ({
  background: "#fff",
  border: "1px solid #dde3e8",
  borderLeft: `4px solid ${LOCATION_STATUS_STYLES[ownerStatus].border}`,
  borderRadius: 4,
  overflow: "hidden",
  boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
  transition: "box-shadow 0.15s",
  marginBottom: 12,
  ...(ownerStatus === "deactivated" && { opacity: 0.75 }),
  "&:hover": {
    boxShadow: "0 2px 10px rgba(0,0,0,0.08)",
  },
}));

/**
 * Compound root component for a supplier location card.
 *
 * Provides a `LocationStatus` context to all sub-components so they can
 * style themselves without prop drilling. `LocationCard.Content` places the
 * parts per breakpoint (mobile-first):
 * - **xs:** full-width 160px photo banner, then header, stacked field groups
 *   and a full-width row of action buttons.
 * - **sm:** 4:3 thumbnail beside the header; field groups side by side;
 *   actions in a right-aligned row at the bottom.
 * - **md:** actions move up to the right of the header.
 * - **lg+:** the photo becomes a 320px 4:3 column beside the whole card.
 *
 * Sub-components:
 * - `LocationCard.Content` — responsive layout grid for all parts below
 * - `LocationCard.Image` — photo (short banner on phones, 4:3 thumbnail from sm) with status ribbon
 * - `LocationCard.Header` — name, address, phone, and a status slot
 * - `LocationCard.Actions` — button slot (children only)
 * - `LocationCard.Fields` — container for field groups
 * - `LocationCard.FieldGroup` — titled group of fields
 * - `LocationCard.Field` — individual label + value cell
 * - `LocationCard.SearchStatusChip` — visible / not-visible pill
 * - `LocationCard.PendingNotice` — amber "in review" bar (Pending status)
 * - `LocationCard.DraftProgress` — completion % bar (Draft status)
 * - `LocationCard.CapacityAlertBadge` — GMS PRO 80% / 90% booked and schedule-ending pills
 *
 * @example
 * ```tsx
 * <LocationCard derivedStatus="active">
 *   <LocationCard.Content>
 *     <LocationCard.Image fileName={location.listingImageFileName} />
 *     <LocationCard.Header
 *       name={location.locationName}
 *       address={`${location.addressLine1}, ${location.city}, ${location.state}`}
 *       phone={location.phoneNumber}
 *       status={<LocationCard.SearchStatusChip location={location} locationReviewCompletion={completion} />}
 *     />
 *     <LocationCard.Actions>
 *       <Button onClick={handleEdit}>Edit</Button>
 *       <Button onClick={handleDeactivate} color="error">Deactivate</Button>
 *     </LocationCard.Actions>
 *     <LocationCard.Fields>
 *       <LocationCard.FieldGroup title="Pricing">
 *         <LocationCard.Field label="Per Day">${location.pricePerDay}</LocationCard.Field>
 *       </LocationCard.FieldGroup>
 *     </LocationCard.Fields>
 *   </LocationCard.Content>
 * </LocationCard>
 * ```
 */
const LocationCard = ({ derivedStatus, children }: LocationCardProps) => (
  <LocationCardContext.Provider value={{ derivedStatus }}>
    <CardRoot ownerStatus={derivedStatus} aria-label={`${derivedStatus} location card`}>
      {children}
    </CardRoot>
  </LocationCardContext.Provider>
);

// Attach sub-components via dot-notation
LocationCard.Image          = LocationCardImage;
LocationCard.Content        = LocationCardContent;
LocationCard.Header         = LocationCardHeader;
LocationCard.Actions        = LocationCardActions;
LocationCard.Fields         = LocationCardFields;
LocationCard.Field          = LocationCardField;
LocationCard.FieldGroup     = LocationCardFieldGroup;
LocationCard.SearchStatusChip  = LocationCardSearchChip;
LocationCard.PendingNotice  = LocationCardPendingNotice;
LocationCard.DraftProgress  = LocationCardDraftProgress;
LocationCard.CapacityAlertBadge = LocationCardCapacityAlertBadge;

export default LocationCard;

